import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';
import { cn } from '@/lib/utils';

const buttonVariants = cva(
  'inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground hover:bg-primary/90',
        destructive:
          'bg-destructive text-destructive-foreground hover:bg-destructive/90',
        outline:
          'border border-input bg-background hover:bg-accent hover:text-accent-foreground',
        secondary:
          'bg-secondary text-secondary-foreground hover:bg-secondary/80',
        ghost: 'hover:bg-accent hover:text-accent-foreground',
        link: 'text-primary underline-offset-4 hover:underline',
        accent:
          'bg-primary text-primary-foreground border border-primary/50 shadow-[0_0_15px_hsl(var(--glow)/0.3)] hover:shadow-[0_0_25px_hsl(var(--glow)/0.5)] hover:bg-primary/90',
        'secondary-action':
          'bg-secondary/80 text-secondary-foreground border border-border/50 hover:bg-secondary hover:border-primary/50 hover:shadow-[0_0_10px_hsl(var(--glow)/0.2)]'
      },
      size: {
        default: 'h-10 px-4 py-2',
        sm: 'h-9 rounded-md px-3',
        lg: 'h-11 rounded-md px-8',
        icon: 'h-10 w-10'
      }
    },
    defaultVariants: {
      variant: 'default',
      size: 'default'
    }
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  enablePressEffect?: boolean;
}

/**
 * Button component with optional press animation on click.
 * Use enablePressEffect={true} for a glow flash effect on interaction.
 */
const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, enablePressEffect = false, onClick, ...props }, ref) => {
    const [isAnimating, setIsAnimating] = React.useState(false);
    const Comp = asChild ? Slot : 'button';
    const shouldAnimate = enablePressEffect;

    const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
      if (shouldAnimate) {
        setIsAnimating(true);
        setTimeout(() => setIsAnimating(false), 400);
      }
      onClick?.(e);
    };

    return (
      <Comp
        className={cn(
          buttonVariants({ variant, size, className }),
          isAnimating && 'animate-button-press'
        )}
        ref={ref}
        onClick={handleClick}
        {...props}
      />
    );
  }
);
Button.displayName = 'Button';

export { Button, buttonVariants };
